// app/javascript/pages/Admin/TermsVersions/Edit.tsx
import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  FileText,
  Eye,
  Edit3
} from 'lucide-react'

interface Props {
  version: {
    id: number
    version_number: string
    title: string
    summary?: string
    content: string
    is_active: boolean
  }
}

export default function TermsVersionsEdit({ version }: Props) {
  const [versionNumber, setVersionNumber] = useState(version.version_number)
  const [title, setTitle] = useState(version.title)
  const [summary, setSummary] = useState(version.summary || '')
  const [content, setContent] = useState(version.content || '')
  const [previewTab, setPreviewTab] = useState<'edit' | 'preview'>('edit')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) {
      alert('Please complete title and terms content.')
      return
    }

    setSubmitting(true)
    router.patch(
      `/admin/terms_versions/${version.id}`,
      {
        terms_version: {
          version_number: versionNumber.trim(),
          title: title.trim(),
          summary: summary.trim(),
          content: content.trim(),
        },
      },
      {
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href={`/admin/terms_versions/${version.id}`}>
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  Edit Terms Version v{version.version_number}
                </h1>
                {version.is_active ? (
                  <Badge variant="default" className="text-xs">Active Version</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Draft</Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Update document title, highlights, and content.
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">
                Metadata
              </CardTitle>
            </CardHeader>

            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="version_number" className="text-xs font-semibold text-foreground">
                    Version Number
                  </Label>
                  <Input
                    id="version_number"
                    value={versionNumber}
                    onChange={(e) => setVersionNumber(e.target.value)}
                    required
                    className="font-mono text-sm"
                  />
                </div>

                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor="title" className="text-xs font-semibold text-foreground">
                    Document Title *
                  </Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary" className="text-xs font-semibold text-foreground">
                  Summary / Change Highlights
                </Label>
                <Input
                  id="summary"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  className="text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Content Editor Card */}
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-foreground">
                  Legal Agreement Content *
                </CardTitle>
              </div>

              <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => setPreviewTab('edit')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    previewTab === 'edit'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Editor</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTab('preview')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                    previewTab === 'preview'
                      ? 'bg-card text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Preview</span>
                </button>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              {previewTab === 'edit' ? (
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={22}
                  required
                  className="w-full font-mono text-xs sm:text-sm p-4 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-hidden focus:ring-1 focus:ring-primary resize-y"
                />
              ) : (
                <div className="p-6 rounded-xl border border-border bg-background min-h-[400px] max-h-[500px] overflow-y-auto text-foreground leading-relaxed">
                  <KatexRenderer
                    content={content || 'Nothing to preview.'}
                    format="markdown"
                    disableExamHelpers
                    className="markdown-content text-xs sm:text-sm leading-relaxed"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Footer */}
          <div className="flex items-center justify-end gap-3">
            <Button asChild variant="outline">
              <Link href={`/admin/terms_versions/${version.id}`}>Cancel</Link>
            </Button>

            <Button type="submit" disabled={submitting} className="gap-2 font-semibold">
              <Save className="w-4 h-4" />
              <span>{submitting ? 'Saving...' : 'Save Changes'}</span>
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  )
}
